import { Box, Typography, useTheme, Select, MenuItem } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar as RBar, BarChart as RBarChart, CartesianGrid as RGrid, ResponsiveContainer as RRC, Tooltip as RTip, XAxis as RXAxis, YAxis as RYAxis, PieChart as RPieChart, Pie as RPie, LineChart as RLineChart, Line as RLine, Treemap as RTreemap, Legend as RLegend, Cell as RCell } from "recharts";
import { GRAPH_PALETTE } from "../../theme/style/graphChartPalette";
import HorizontalStackedBarChart from "./HorizontalStackedBarChart";
import ScatterChart from "./ScatterChart";
import DonutChart from "./DonutChart";
import { LuX } from "react-icons/lu";
import { getOrgTokens } from "../../theme/style/organizationPalette";

// ─── Metric catalogue using strict GRAPH_PALETTE ──────────────────────────────
export const STUDIO_METRICS = [
  { key: "gwp", label: "GWP", unit: "kg CO₂e", color: GRAPH_PALETTE.lcaStages.A3 },
  { key: "cost", label: "Cost", unit: "$", color: GRAPH_PALETTE.lcaStages.A4 },
  { key: "biogenic_gwp", label: "Biogenic GWP", unit: "kg CO₂e", color: GRAPH_PALETTE.lcaStages.D },
  { key: "reused_gwp", label: "Reused GWP", unit: "kg CO₂e", color: GRAPH_PALETTE.lcaStages.B1 },
  { key: "ap", label: "AP", unit: "kg SO₂e", color: GRAPH_PALETTE.lcaStages.A2 },
  { key: "odp", label: "ODP", unit: "kg CFC-11e", color: GRAPH_PALETTE.lcaStages.C1 },
  { key: "ep_marine", label: "EP-marine", unit: "kg N", color: GRAPH_PALETTE.lcaStages.A1 },
  { key: "cost_per_gwp", label: "Cost/GWP", unit: "$/kg", color: GRAPH_PALETTE.lcaStages.Biogenic },
];

const CHART_TYPES = [
  { key: "horizontal_bar", label: "H-Bar" },
  { key: "vertical_bar", label: "V-Bar" },
  { key: "scatter", label: "Scatter" },
  { key: "quadrant", label: "Quadrant" },
  { key: "donut", label: "Donut" },
  { key: "pie", label: "Pie" },
  { key: "line", label: "Line" },
  { key: "treemap", label: "TreeMap" },
];

export const getMetricValue = (item, key) => {
  if (key === "cost_per_gwp") {
    const gwp = Number(item.gwp) || 0;
    return gwp > 0 ? (Number(item.cost) || 0) / gwp : 0;
  }
  return Number(item[key]) || 0;
};

// ─── Component ────────────────────────────────────────────────────────────────
const ChartStudio = ({ items = [], entityLabel = "Items", defaultMetric = "gwp" }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Theme extractions adapting to Light/Dark Mode
  const isDark = theme.palette.mode === 'dark';
  const tok = getOrgTokens(isDark);
  const brandGreen = theme.palette.primary?.main || "#659d33"; // Keep brandGreen from theme palette for consistency

  // Sync these from standard palette
  const borderColor = tok.border.card;
  const bgCard = tok.bg.card;
  const textTitle = tok.text.primary;
  const textSub = tok.text.secondary;
  const bgSurface = isDark ? "rgba(0,0,0,0.2)" : "#f1f5f9"; // Soft pill backgrounds

  const [chartType, setChartType] = useState("horizontal_bar");
  const [yMetric, setYMetric] = useState(defaultMetric);
  const [xMetric, setXMetric] = useState("cost");
  const [topN, setTopN] = useState(5);
  const [dragging, setDragging] = useState(null);
  const [dragOverZone, setDragOverZone] = useState(null);

  const isScatter = chartType === "scatter" || chartType === "quadrant";
  const xDef = STUDIO_METRICS.find(m => m.key === xMetric) || STUDIO_METRICS[0];
  const yDef = STUDIO_METRICS.find(m => m.key === yMetric) || STUDIO_METRICS[0];

  const chartDataWrapper = useMemo(() => {
    let src = items.filter(item => getMetricValue(item, yMetric) > 0 || (isScatter && getMetricValue(item, xMetric) > 0));
    if (src.length === 0) return { data: [], means: null };

    if (isScatter) {
      src = src.filter(item => getMetricValue(item, yMetric) >= 0 && getMetricValue(item, xMetric) >= 0);
      let meanX = 0; let meanY = 0;
      if (src.length > 0) {
        meanX = src.reduce((acc, curr) => acc + getMetricValue(curr, xMetric), 0) / src.length;
        meanY = src.reduce((acc, curr) => acc + getMetricValue(curr, yMetric), 0) / src.length;
      }
      const mapped = src.map((item, i) => {
        let name = item.name || `Item ${i + 1}`;
        if (name.length > 50) name = name.substring(0, 50) + "...";
        return { id: item.id, label: name, [xMetric]: getMetricValue(item, xMetric), [yMetric]: getMetricValue(item, yMetric), z: 700 };
      });
      return { data: mapped, means: { x: meanX, y: meanY } };
    }

    const limit = topN === "all" ? undefined : Number(topN);
    const mapped = [...src]
      .sort((a, b) => getMetricValue(b, yMetric) - getMetricValue(a, yMetric))
      .slice(0, limit)
      .map((item, i) => {
        let name = item.name || `Item ${i + 1}`;
        if (name.length > 40) name = name.substring(0, 40) + "...";
        return { id: item.id, name: name, label: name, [yMetric]: getMetricValue(item, yMetric), value: getMetricValue(item, yMetric) };
      });
    return { data: mapped, means: null };
  }, [items, xMetric, yMetric, topN, isScatter]);

  const { data: chartData, means } = chartDataWrapper;

  // New helper for dynamic vertical scaling
  const calculatedHeight = useMemo(() => {
    if (chartType === 'vertical_bar' || chartType === 'line') {
      // Base padding + (data count * width per bar)
      // 300px for labels/margins + 25px per item
      return Math.max(600, chartData.length * 30 + 350);
    }
    if (chartType === 'horizontal_bar') {
      return Math.max(500, chartData.length * 45 + 150);
    }
    return 600;
  }, [chartType, chartData]);

  // ── Sub-Components (Clean, No Neon) ───────────────────────────────────────

  const MetricPill = ({ m, isActive }) => (
    <Box
      draggable onDragStart={() => setDragging(m.key)} onDragEnd={() => { setDragging(null); setDragOverZone(null); }}
      onClick={() => isScatter ? (!xMetric ? setXMetric(m.key) : setYMetric(m.key)) : setYMetric(m.key)}
      sx={{
        display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.5,
        borderRadius: "8px", cursor: "grab", userSelect: "none",
        border: `1px solid ${isActive ? m.color : borderColor}`,
        bgcolor: isActive ? `${m.color}15` : bgSurface,
        boxShadow: isActive ? "0 2px 4px rgba(0,0,0,0.05)" : 'none',
        transition: "all 0.2s",
        "&:hover": { borderColor: isActive ? m.color : textSub },
      }}
    >
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isActive ? m.color : textSub }} />
      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "12px", fontWeight: 600, color: isActive ? (isDark ? m.color : textTitle) : textSub }}>{m.label}</Typography>
      <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: "10px", color: textSub, opacity: 0.8 }}>{m.unit}</Typography>
    </Box>
  );

  const AxisDropZone = ({ zone, activeDef, label }) => {
    const isHovered = dragOverZone === zone;
    return (
      <Box
        onDragOver={e => { e.preventDefault(); setDragOverZone(zone); }}
        onDragLeave={() => setDragOverZone(null)}
        onDrop={e => {
          e.preventDefault();
          if (dragging) { zone === "x" ? setXMetric(dragging) : setYMetric(dragging); }
          setDragOverZone(null); setDragging(null);
        }}
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, px: 2, height: '36px',
          border: `1px dashed ${isHovered ? brandGreen : borderColor}`,
          borderRadius: "8px",
          bgcolor: isHovered ? `${brandGreen}10` : 'transparent',
          transition: 'all 0.2s', minWidth: '150px'
        }}
      >
        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: textSub, letterSpacing: '1px' }}>{label}</Typography>
        {activeDef ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: activeDef.color }} />
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: textTitle }}>{activeDef.label}</Typography>
            <Typography sx={{ fontSize: "10px", fontFamily: GRAPH_PALETTE.font.family, color: textSub }}>{activeDef.unit}</Typography>
            <Box component={LuX} sx={{ cursor: 'pointer', color: textSub, '&:hover': { color: '#ef4444' }, ml: 1, fontSize: '14px' }} onClick={() => zone === 'x' ? setXMetric(null) : setYMetric(null)} />
          </Box>
        ) : (
          <Typography sx={{ fontSize: "11px", fontStyle: "italic", color: textSub, opacity: 0.5 }}>drop metric</Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          borderRadius: tok.radius.card,
          overflow: 'hidden',
          border: `1px solid ${tok.border.card}`,
          bgcolor: tok.bg.card,
          boxShadow: tok.shadow.card,
        }}
      >

        {/* ── 1. Header Area ────────────────────────────────────────────────── */}
        <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${borderColor}`, flexWrap: 'wrap', gap: 2 }}>

          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: textTitle,
              fontFamily: tok.font.family,
              m: 0,
              lineHeight: 1
            }}
          >
            Chart Studio
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ display: "flex", gap: 0.5, bgcolor: bgSurface, p: 0.5, borderRadius: "8px" }}>
              {CHART_TYPES.map(ct => {
                const isActive = chartType === ct.key;
                return (
                  <Box
                    key={ct.key} onClick={() => setChartType(ct.key)}
                    sx={{
                      px: 2, py: 0.5, borderRadius: "6px", cursor: "pointer", fontSize: tok.font.size.tabLabel, fontWeight: isActive ? 600 : 500,
                      color: isActive ? "#ffffff" : textSub, bgcolor: isActive ? brandGreen : "transparent",
                      boxShadow: isActive ? tok.shadow.card : "none", transition: "all 0.2s", fontFamily: tok.font.family,
                      userSelect: "none",
                    }}
                  >
                    {t(ct.label)}
                  </Box>
                );
              })}
            </Box>

            <Select
              value={topN} onChange={e => setTopN(e.target.value)} size="small"
              sx={{
                height: '32px', fontSize: '12px', bgcolor: bgSurface, color: textTitle, fontFamily: GRAPH_PALETTE.font.family, borderRadius: '8px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borderColor },
                '& .MuiSvgIcon-root': { color: textSub }
              }}
            >
              {[3, 5, 10, 15].map(n => <MenuItem key={n} value={n} sx={{ fontSize: '12px' }}>Top {n}</MenuItem>)}
              <MenuItem value="all" sx={{ fontSize: '12px' }}>All</MenuItem>
            </Select>
          </Box>
        </Box>

        {/* ── 2. Metrics & Drop Zone Area ─────────────────────────────────────── */}
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, flex: 1 }}>
            {STUDIO_METRICS.map(m => (
              <MetricPill key={m.key} m={m} isActive={yMetric === m.key || (isScatter && xMetric === m.key)} />
            ))}
          </Box>

          <Box sx={{ width: '1px', height: '24px', bgcolor: borderColor, display: { xs: 'none', md: 'block' } }} />

          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {isScatter ? (
              <>
                <AxisDropZone zone="x" activeDef={xDef} label="X" />
                <AxisDropZone zone="y" activeDef={yDef} label="Y" />
              </>
            ) : (
              <AxisDropZone zone="y" activeDef={yDef} label="VAL" />
            )}
          </Box>
        </Box>

        {/* ── 3. Chart Rendering Area ─────────────────────────────────────────── */}
        <Box sx={{ p: 5, height: calculatedHeight, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: bgCard }}>
          {chartData.length === 0 ? (
            <Typography sx={{ color: textSub, fontFamily: GRAPH_PALETTE.font.family }}>No valid data for this view</Typography>
          ) : isScatter ? (
            <ScatterChart
              data={chartData}
              labelKey="label"
              singleSeries={true}
              height={500}
              x={{ type: "number", dataKey: xMetric, name: xDef.label, unit: xDef.unit }}
              y={{ type: "number", dataKey: yMetric, name: yDef.label, unit: yDef.unit }}
              z={{ type: "number", dataKey: "z", name: "Volume" }}
              referenceLines={chartType === "quadrant" && means ? [{ axis: "x", value: means.x, label: "Mean X", dashed: true }, { axis: "y", value: means.y, label: "Mean Y", dashed: true }] : []}
              referenceAreas={chartType === "quadrant" && means ? [
                { x1: means.x, y1: means.y, fill: isDark ? "rgba(239,68,68,0.1)" : "rgba(239,68,68,0.05)" },
                { x2: means.x, y2: means.y, fill: isDark ? "rgba(34,197,94,0.1)" : "rgba(34,197,94,0.05)" },
                { x2: means.x, y1: means.y, fill: isDark ? "rgba(245,158,11,0.1)" : "rgba(245,158,11,0.05)" },
                { x1: means.x, y2: means.y, fill: isDark ? "rgba(59,130,246,0.1)" : "rgba(59,130,246,0.05)" }
              ] : []}
            />
          ) : chartType === "horizontal_bar" ? (
            <HorizontalStackedBarChart
              data={chartData}
              dataKeys={[yMetric]}
              getColor={() => yDef.color}
              showLegend={false}
              height={calculatedHeight - 100}
              barSize={20}
              xAxisLabel={`${yDef.label} (${yDef.unit})`}
              yAxisLabel={entityLabel}
            />
          ) : chartType === "donut" ? (
            <DonutChart
              data={chartData.map((d, i) => ({ label: d.label, value: d.value, color: [yDef.color, GRAPH_PALETTE.lcaStages.A1, GRAPH_PALETTE.lcaStages.A2, GRAPH_PALETTE.lcaStages.B1, GRAPH_PALETTE.lcaStages.C1][i % 5] }))}
              size={Math.min(320, window.innerWidth - 100)}
              strokeWidth={25}
              centerContent={
                <Box sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.donutTotalSmall, fontWeight: 500, color: textSub, textTransform: 'uppercase', letterSpacing: '1px' }}>Total</Typography>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.donutTotalBig, fontWeight: 700, color: textTitle, lineHeight: 1.1 }}>
                    {chartData.reduce((acc, curr) => acc + (curr.value || 0), 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </Typography>
                  <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: '12px', color: textSub, fontWeight: 500 }}>{yDef.unit}</Typography>
                </Box>
              }
            />
          ) : chartType === "pie" ? (
            <RRC width="100%" height="100%">
              <RPieChart>
                <RTip formatter={v => [Number(v).toFixed(2), yDef.label]} contentStyle={{ borderRadius: 8, border: `1px solid ${borderColor}`, backgroundColor: bgCard, color: textTitle, fontFamily: GRAPH_PALETTE.font.family, fontSize: '14px' }} />
                <RPie
                  data={chartData}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={Math.min(160, calculatedHeight / 3)}
                  paddingAngle={2}
                  label={({ percent }) => percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ""}
                  labelLine={false}
                  style={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: '13px', fontWeight: 600 }}
                >
                  {chartData.map((entry, index) => (
                    <RCell key={`cell-${index}`} fill={[yDef.color, "#ef4444", "#f59e0b", "#0ea5e9", "#22c55e", "#14b8a6", "#ec4899", "#8b5cf6"][index % 8]} />
                  ))}
                </RPie>
                <RLegend layout="horizontal" verticalAlign="bottom" align="center" wrapperStyle={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: '11px', paddingTop: '20px' }} />
              </RPieChart>
            </RRC>
          ) : chartType === "line" ? (
            <RRC width="100%" height="100%">
              <RLineChart data={chartData} margin={{ top: 20, right: 30, bottom: 200, left: 60 }}>
                <RGrid strokeDasharray="3 3" vertical={false} stroke={borderColor} opacity={0.5} />
                <RXAxis dataKey="label" tick={{ fill: textSub, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard), fontFamily: GRAPH_PALETTE.font.family }} angle={-45} textAnchor="end" dy={10} dx={-5} interval={0} axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }} tickLine={false} />
                <RYAxis tick={{ fill: textSub, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard), fontFamily: GRAPH_PALETTE.font.family }} axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }} tickLine={false} />
                <RTip formatter={v => [Number(v).toFixed(2), yDef.label]} contentStyle={{ borderRadius: 8, border: `1px solid ${borderColor}`, backgroundColor: bgCard, color: textTitle, fontFamily: GRAPH_PALETTE.font.family, fontSize: '14px' }} />
                <RLine type="monotone" dataKey={yMetric} stroke={yDef.color} strokeWidth={3} dot={{ r: 4, fill: bgCard, stroke: yDef.color, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              </RLineChart>
            </RRC>
          ) : chartType === "treemap" ? (
            <RRC width="100%" height="100%">
              <RTreemap
                data={chartData}
                dataKey="value"
                aspectRatio={4 / 3}
                stroke={bgCard}
                style={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard }}
              >
                {chartData.map((entry, index) => (
                  <RCell key={`cell-${index}`} fill={[yDef.color, "#ef4444", "#f59e0b", "#0ea5e9", "#22c55e", "#14b8a6", "#ec4899", "#8b5cf6"][index % 8]} />
                ))}
                <RTip formatter={v => [Number(v).toFixed(2), yDef.label]} contentStyle={{ borderRadius: 8, border: `1px solid ${borderColor}`, backgroundColor: bgCard, color: textTitle, fontFamily: GRAPH_PALETTE.font.family, fontSize: '14px' }} />
              </RTreemap>
            </RRC>
          ) : (
            <RRC width="100%" height="100%">
              <RBarChart data={chartData} margin={{ top: 20, right: 30, bottom: 200, left: 60 }}>
                <RGrid strokeDasharray="3 3" vertical={false} stroke={borderColor} opacity={0.5} />
                <RXAxis dataKey="label" tick={{ fill: textSub, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard), fontFamily: GRAPH_PALETTE.font.family }} angle={-45} textAnchor="end" dy={10} dx={-10} interval={0} axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }} tickLine={false} />
                <RYAxis tick={{ fill: textSub, fontSize: parseInt(GRAPH_PALETTE.font.sizes.standard), fontFamily: GRAPH_PALETTE.font.family }} axisLine={{ stroke: isDark ? GRAPH_PALETTE.axes.darkMode.line : GRAPH_PALETTE.axes.lightMode.line, strokeWidth: GRAPH_PALETTE.axes.strokeWidth }} tickLine={false} />
                <RTip cursor={{ fill: `${textSub}10` }} contentStyle={{ borderRadius: 8, border: `1px solid ${borderColor}`, backgroundColor: bgCard, color: textTitle, fontFamily: GRAPH_PALETTE.font.family, fontSize: '14px' }} />
                <RBar dataKey={yMetric} radius={[4, 4, 0, 0]} barSize={30}>
                  {chartData.map((_, i) => <RCell key={i} fill={yDef.color} />)}
                </RBar>
              </RBarChart>
            </RRC>
          )}
        </Box>

        {/* Chart X-Axis Label */}
        {chartData.length > 0 && chartType !== 'pie' && chartType !== 'donut' && (
          <Box sx={{ textAlign: 'center', pb: 3, bgcolor: bgCard }}>
            <Typography sx={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.mainAxisTitle, color: textSub, letterSpacing: '1px', textTransform: 'none', fontWeight: 600 }}>
              {yDef.label} — {yDef.unit}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChartStudio;